<template>
  <div class="valuesWrapper">
    <div v-if="isAdmin(account)" class="columns">
      <div class="column">
        <button
            type="button"
            class="button"
            @click="toggleImportValues()">

              <span class="icon is-small">
                <i class="fas fa-upload"></i>
              </span>
          <span>Basislijstwaardes importeren</span>
        </button>

        <hr>
      </div>
    </div>
    <div class="columns">
      <div class="column is-one-quarter">
        <aside class="menu">
          <ul>
            <li
              v-for="category in columnCategoriesTree"
              v-show="used(category.columns).length > 0"
              :key="category.id">

              <p @click="toggleCategory(category.id)">
                <span class="icon">
                  <i class="fas" :class="categoryIsActive(category.id) ? 'fa-folder-open' : 'fa-folder'"></i>
                </span>

                <strong>{{ category.name }}</strong>
              </p>

              <ul :class="{ 'is-hidden': !categoryIsActive(category.id) }" class="menu-list">
                <li v-for="column in used(category.columns)" :key="column.id">
                  <a :class="{ 'is-active': getColumnIndex(column.id) === selected }" @click="select(getColumnIndex(column.id))">{{ column.tabname }}</a>
                </li>
              </ul>
            </li>
          </ul>
        </aside>
      </div>

      <div class="column is-three-quarters">
        <section>
          <table class="table is-striped is-fullwidth">
            <thead>
              <tr>
                <th>
                  <toggle-all
                    v-show="selected !== null"
                    :checked="allSelected"
                    :indeterminate="someSelected"
                    @toggle="toggleAllSelected()">
                  </toggle-all>
                </th>
                <th>{{ selected !== null ? columns[selected].tabname: 'Selecteer links een kolom' }}</th>
                <th class="offset">
                  <options-buttons
                    :pullRight="true"
                    :editing="false"
                    :deleting="showMassRemoveButton"
                    deleteTitle="Geselecteerde waardes verwijderen"
                    @delete="massRemove">
                  </options-buttons>
                </th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="(value, index) in values" :key="value.id">
                <!-- FIXME: State is being modified by the component -->
                <template v-if="editing === index">
                  <td></td>
                  <td>
                    <input v-if="value.value.length < 50" name="value" v-model="value.value" type="text" class="input">
                    <textarea v-else class="textarea" name="value" v-model="value.value"></textarea>
                  </td>
                  <td>
                    <options-buttons
                      :pullRight="true"
                      :saving="true"
                      :cancelling="true"
                      :editing="false"
                      :deleting="false"
                      @save="save(value)"
                      @cancel="cancel"></options-buttons>
                  </td>
                </template>

                <template v-else>
                  <td>
                    <toggle-single
                      :checked="isRemovable(value.id)"
                      @toggle="toggleRemove(value.id)">
                    </toggle-single>
                  </td>
                  <td>{{ trim(value.value) }}</td>
                  <td>
                    <options-buttons
                      v-if="!belongsToDossier(value)"
                      :pullRight="true"
                      @edit="edit(index)"
                      @delete="remove(value.id)"></options-buttons>
                  </td>
                </template>

              </tr>
            </tbody>
          </table>

          <template v-if="notQuestions">
            <template v-if="newValue && newValue.length > 70">
              <div class="field">
                <div class="control">
                  <textarea
                    name="value"
                    v-model="newValue"
                    class="textarea"
                    @keyup.ctrl.enter="create(newValue)"></textarea>
                </div>
                <p class="help">Gebruik Ctrl+Enter om op te slaan.</p>
              </div>

              <div class="control">
                <button
                  class="button is-info"
                  type="button"
                  :disabled="!newValue"
                  @click="create(newValue)">
                  Opslaan
                </button>
              </div>
            </template>

            <div v-else class="field has-addons">
              <div class="control is-expanded">
                <input
                  name="value"
                  v-model="newValue"
                  type="text"
                  class="input"
                  :placeholder="selected !== null ? 'Voer een waarde in of genereer een reeks met onderstaande syntax' : 'Kies eerst links een kolom om waardes toe te kennen'"
                  :disabled="selected === null"
                  @keyup.enter="create(newValue)">
              </div>

              <div class="control">
                <button class="button is-info" type="button" @click="create(newValue)" :disabled="!newValue">
                  <i class="fas fa-save"></i>
                </button>
              </div>
            </div>
          </template>

          <div v-else class="notification is-warning">
            De {{ columns[selected].tabname.toLowerCase() }} kolom is een speciale kolom waarvan de basislijst alleen gevuld kan worden door {{ columns[selected].name.toLowerCase() }} toe te voegen aan het kwalificatiedossier dat is gekoppeld aan deze bundel. Voor het toevoegen van {{ columns[selected].name.toLowerCase() }} via het kwalificatiedossier moeten speciale gebruikersrechten worden toegekend door de beheerder aan uw account.
          </div>

          <p class="help"><strong>Serie invoeren:</strong>
            <br>Gebruik "{a,b,c,n}" zonder de aanhalingstekens, bijv:
            <br>- "{Woord 1,Woord 2,Woord 3}" maakt 3 waardes aan genaamd Woord 1, Woord 2 en Woord 3
            <br>- Let op dat spaties na de komma worden verwerkt in de invoerwaarde!
          <p>

          <p class="help"><strong>Serie genereren:</strong>
            <br>Gebruik "{start..einde:interval:p=prefix:s=suffix}" zonder de aanhalingstekens, bijv:
            <br>- "{1..10}" maakt een serie getallen aan van 1 tot 10
            <br>- "{1..5:0.2}" maakt een serie aan van 1 tot 5 met intervallen van 0,2
            <br>- "{10..20:p=Tekst}" maakt een serie getallen aan van 10 tot 20 met het woord "Tekst" ervoor
            <br>- "{10..20:s=Tekst}" maakt een serie getallen aan van 10 tot 20 met het woord "Tekst" erachter
            <br>- gebruik spaties waar gewenst, ook om de prefix en/of suffix van de waarde te scheiden, {1..2:p=Geen} => Geen1, Geen2 / {1..2:p=Wel } => Wel 1, Wel 2
          </p>

          <hr>

          <button
            type="button"
            class="button"
            :class="{ 'is-success': copy }"
            :disabled="!exportedValues"
            v-clipboard:copy="exportedValues"
            v-clipboard:success="onCopy"
            v-clipboard:error="onError">
            <span class="icon is-small">
              <i class="fas fa-clone"></i>
            </span>
            <span>De waardes van deze kolom exporteren</span>
          </button>

        </section>
      </div>
    </div>

    <import-values
      :isActive="isActive"
      @close="toggleImportValues()"></import-values>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import OptionsButtons from '@/components/modules/OptionsButtons'
import ToggleAll from '@/components/modules/mass-remove/ToggleAll'
import ToggleSingle from '@/components/modules/mass-remove/ToggleSingle'
import { authMixin } from '@/mixins'
import ImportValues from "@/components/values/ImportValues";

export default {
  name: 'Values',
  components: {
    ImportValues,
    ToggleAll,
    ToggleSingle,
    OptionsButtons,
  },
  mixins: [
    authMixin
  ],
  data () {
    return {
      copy: null,
      editing: null,
      areActive: [],
      loading: false,
      selected: null,
      newValue: null,
      removables: [],
      isActive: false,
    }
  },
  computed: {
    ...mapGetters({
      values: 'values/values',
      tables: 'tables/tables',
      bundle: 'bundles/bundle',
      columns: 'columns/columns',
      account: 'account/account',
      columnCategoriesTree: 'columnCategories/columnCategoriesTree'
    }),
    exportedValues () {
      if (!this.values || this.values.length === 0) return false

      let values = this.values.map((value) => { return value.value })

      return `{${values.join(',')}}`
    },
    notQuestions () {
      if (this.selected) {
        return this.columns[this.selected].id !== 34
      }

      return true
    },
    valueIds () {
      return this.values.map(x => x.id)
    },
    allSelected () {
      if (this.valueIds.length === 0) return false
      return this.valueIds.length === this.removables.length
    },
    someSelected () {
      if (this.allSelected) return false

      return this.valueIds.filter((valueId) => {
        return this.removables.includes(valueId)
      }).length > 0
    },
    showMassRemoveButton () {
      return this.selected !== null &&
        (this.allSelected === true || this.someSelected === true)
    },
    visibleTables () {
      return (this.bundle) ? this.tables.filter(table => !this.bundle.has_disabled['tables'].includes(table.id)) : []
    },
    usedColumns () {
      const columnIds = this.visibleTables.map(table => table.column_ids).flat().sort((a,b) => a-b)

      return [...new Set(columnIds)]
    }
  },
  watch: {
    selected () {
      if (this.selected !== null) this.fetchValues()
    }
  },
  created () {
    this.clear()
  },
  beforeDestroy () {
    this.$store.commit('columns/RESET')
  },
  methods: {
    used (columns) {
      return columns.filter(column => this.usedColumns.includes(column.id))
    },
    onCopy () {
      this.copy = true
    },
    onError () {
      this.copy = false
    },
    trim (value) {
      if (value.length > 80) return `${value.substring(0, 80)}...`
      return value
    },
    clear () {
      this.selected = null
      this.$store.dispatch('columns/loadAll')
      this.$store.commit('values/CLEAR')
    },
    fetchValues () {
      this.$store.commit('values/CLEAR')
      this.$store.dispatch('values/loadAll', {
        bundleId: this.$route.params.bundleId,
        columnId: this.columns[this.selected].id
      })
    },
    select (index) {
      this.selected = index
    },
    edit (index) {
      this.editing = index
    },
    cancel () {
      this.editing = null
    },
    isSerie () {
      return /^{(.*)}$/.test(this.newValue)
    },
    isWords () {
      return /^{(.*),(.*)}/.test(this.newValue)
    },
    parse () {
      if (!this.isSerie()) {
        this.newValue = [{ value: this.newValue }]
        return
      }

      let stripped = this.newValue.slice(1, -1)

      if (this.isWords()) {
        this.newValue = stripped.split(',').map(value => { return { value: value } })
      } else {
        let start = null
        let end = null
        let prefix = null
        let suffix = 0
        let interval = 1
        let exp = stripped.split(':')

        let bounds = /^([0-9]+)..([0-9]+)$/.exec(exp[0])

        if (bounds.length === 3) {
          start = parseInt(bounds[1])
          end = parseInt(bounds[2])
        } else {
          return false
        }

        if (exp.length > 1) {
          for (let i = 1; i < exp.length; i++) {
            if (exp[i].charAt(0) === 'p') {
              prefix = /=(.*)$/.exec(exp[i])
            } else if (exp[i].charAt(0) === 's') {
              suffix = /=(.*)$/.exec(exp[i])
            } else {
              interval = parseFloat(exp[i])
            }
          }
        }

        let value = start
        let values = new Array(Math.round((end - start) / interval) + 1)

        for (let i = 0; i < values.length; i++) {
          let str = ''
          if (prefix) str = prefix[1]
          str += Math.round(value * 10000) / 10000
          if (suffix) str += suffix[1]
          values[i] = str
          value = value + interval
        }

        this.newValue = values.map(value => { return { value: value } })
      }
    },
    save (value) {
      this.$store.dispatch('values/save', {
        values: [value]
      })

      this.editing = null
    },
    create () {
      this.parse()

      this.$store.dispatch('values/create', {
        values: this.newValue,
        bundles: [this.bundle.id],
        columnId: this.columns[this.selected].id
      })

      this.newValue = null
    },
    remove (valueId) {
      this.$store.dispatch('values/remove', { values: [valueId] })
    },
    massRemove () {
      this.$store.dispatch('values/remove', { values: this.removables })
      this.removables = []
    },
    belongsToDossier (value) {
      return value.number !== null
    },
    toggleRemove (valueId) {
      if (this.isRemovable(valueId)) {
        this.removables.splice(this.getRemovableIndex(valueId), 1)
      } else {
        this.removables.push(valueId)
      }
    },
    toggleAllSelected () {
      if (this.allSelected) {
        this.clearRemovables()
      } else {
        this.clearRemovables()
        this.valueIds.forEach(valueId => this.removables.push(valueId))
      }
    },
    toggleImportValues () {
      this.isActive = !this.isActive
    },
    clearRemovables () {
      this.removables = []
    },
    getRemovableIndex (valueId) {
      return this.removables.findIndex(x => x === valueId)
    },
    getColumnIndex (columnId) {
      return this.columns.findIndex(x => x.id === columnId)
    },
    isRemovable (valueId) {
      return this.getRemovableIndex(valueId) !== -1
    },
    categoryIsActive (categoryId) {
      return this.areActive.includes(categoryId)
    },
    toggleCategory (categoryId) {
      const idx = this.areActive.findIndex(x => x === categoryId)

      if (idx !== -1) {
        this.areActive.splice(idx, 1)
      } else {
        this.areActive.push(categoryId)
      }
    }
  }
}
</script>
